import React, { useState } from 'react';
import { Stepper } from '@progress/kendo-react-layout';
import { authorizationApi } from '../../api/authorizationApi';
import { isApiConfigured } from '../../api/axiosClient';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const initialFormData = {
  memberId: '',
  memberName: '',
  service: '',
  priority: 'Standard',
  dateFrom: '',
  dateThrough: '',
  phone: '',
  fax: '',
  procedureCode: '',
  procedureDescription: '',
  diagnosisCode: '',
  diagnosisDescription: '',
  clinicalNotes: '',
};

const steps = [
  { label: 'Authorization Basic', icon: 'k-i-user' },
  { label: 'Procedure & Diagnosis', icon: 'k-i-clipboard-text' },
  { label: 'Clinical Info', icon: 'k-i-check-outline' },
];

const AuthorizationRequestPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const validateStep = (step) => {
    if (step === 0) {
      if (!formData.memberId.trim()) return 'Member ID is required.';
      if (!formData.memberName.trim()) return 'Member Name is required.';
      if (!formData.service) return 'Please select a service.';
      if (!formData.dateFrom) return 'Date Requested From is required.';
      if (!formData.dateThrough) return 'Date Requested Through is required.';
    }
    if (step === 1) {
      if (!formData.procedureCode.trim()) return 'Procedure Code is required.';
      if (!formData.procedureDescription.trim()) return 'Procedure Description is required.';
      if (!formData.diagnosisCode.trim()) return 'Diagnosis Code is required.';
      if (!formData.diagnosisDescription.trim()) return 'Diagnosis Description is required.';
    }
    return '';
  };

  const handleNext = () => {
    const validationError = validateStep(currentStep);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setError('');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitError('');
    setSubmitting(true);

    if (isApiConfigured()) {
      try {
        await authorizationApi.submit(formData);
        setSubmitted(true);
      } catch (err) {
        setSubmitError(err.message || 'Failed to submit authorization request. Please try again.');
      }
    } else {
      setSubmitted(true);
    }

    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="main-content">
        <div className="page-header">
          <div>
            <h1>Prior Authorization Request</h1>
          </div>
        </div>
        <div
          className="card"
          style={{
            background: 'rgba(16, 185, 129, 0.08)',
            borderLeft: '4px solid #10B981',
            textAlign: 'center',
            padding: 40,
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
          <h2
            style={{
              margin: '0 0 8px 0',
              color: '#059669',
              fontSize: 22,
              fontWeight: 700,
            }}
          >
            Authorization Request Submitted
          </h2>
          <p style={{ color: '#6B7280', fontSize: 14, margin: '0 0 24px 0' }}>
            Your prior authorization request has been submitted successfully. You
            will receive a decision within 3-5 business days.
          </p>
          <button
            className="btn-primary"
            onClick={() => {
              setSubmitted(false);
              setCurrentStep(0);
              setFormData(initialFormData);
            }}
          >
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1>Prior Authorization Request</h1>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <Stepper value={currentStep} items={steps} />
      </div>

      {error && (
        <div style={{
          background: '#FEE2E2', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', padding: '10px 14px',
          fontSize: 13, fontWeight: 500, color: '#EF4444', marginBottom: 16,
        }}>
          {error}
        </div>
      )}

      {submitError && (
        <div style={{
          background: '#FEE2E2', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', padding: '10px 14px',
          fontSize: 13, fontWeight: 500, color: '#EF4444', marginBottom: 16,
        }}>
          {submitError}
        </div>
      )}

      {currentStep === 0 && (
        <div className="card">
          <h3>Step 1: Authorization Basic Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="required">Member ID</label>
              <input
                type="text"
                value={formData.memberId}
                onChange={(e) => handleChange('memberId', e.target.value)}
                placeholder="e.g. SND-100234"
              />
            </div>

            <div className="form-group">
              <label className="required">Member Name</label>
              <input
                type="text"
                value={formData.memberName}
                onChange={(e) => handleChange('memberName', e.target.value)}
                placeholder="Enter member full name"
              />
            </div>

            <div className="form-group">
              <label className="required">Select Service</label>
              <select
                value={formData.service}
                onChange={(e) => handleChange('service', e.target.value)}
              >
                <option value="">-- Select Service --</option>
                <option value="MRI">MRI</option>
                <option value="CT Scan">CT Scan</option>
                <option value="Physical Therapy">Physical Therapy</option>
                <option value="Outpatient Surgery">Outpatient Surgery</option>
                <option value="Specialist Consultation">Specialist Consultation</option>
                <option value="Sleep Study">Sleep Study</option>
                <option value="Cardiac Testing">Cardiac Testing</option>
              </select>
            </div>

            <div className="form-group">
              <label className="required">Select Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                <option value="Standard">Standard</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            <div className="form-group">
              <label className="required">Date Requested From</label>
              <input
                type="date"
                value={formData.dateFrom}
                onChange={(e) => handleChange('dateFrom', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="required">Date Requested Through</label>
              <input
                type="date"
                value={formData.dateThrough}
                onChange={(e) => handleChange('dateThrough', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(512) 555-0000"
              />
            </div>

            <div className="form-group">
              <label>Fax</label>
              <input
                type="tel"
                value={formData.fax}
                onChange={(e) => handleChange('fax', e.target.value)}
                placeholder="(512) 555-0000"
              />
            </div>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <div className="card">
          <h3>Step 2: Procedure & Diagnosis</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="required">Procedure Code</label>
              <input
                type="text"
                value={formData.procedureCode}
                onChange={(e) => handleChange('procedureCode', e.target.value)}
                placeholder="e.g. 72148"
              />
            </div>

            <div className="form-group">
              <label className="required">Procedure Description</label>
              <input
                type="text"
                value={formData.procedureDescription}
                onChange={(e) =>
                  handleChange('procedureDescription', e.target.value)
                }
                placeholder="e.g. MRI Lumbar Spine without Contrast"
              />
            </div>

            <div className="form-group">
              <label className="required">Diagnosis Code (ICD-10)</label>
              <input
                type="text"
                value={formData.diagnosisCode}
                onChange={(e) => handleChange('diagnosisCode', e.target.value)}
                placeholder="e.g. M54.5"
              />
            </div>

            <div className="form-group">
              <label className="required">Diagnosis Description</label>
              <input
                type="text"
                value={formData.diagnosisDescription}
                onChange={(e) =>
                  handleChange('diagnosisDescription', e.target.value)
                }
                placeholder="e.g. Low Back Pain"
              />
            </div>

            <div className="form-group form-full">
              <label>Clinical Notes</label>
              <textarea
                rows={4}
                value={formData.clinicalNotes}
                onChange={(e) => handleChange('clinicalNotes', e.target.value)}
                placeholder="Enter clinical justification and relevant notes..."
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="card">
          <h3>Step 3: Review & Submit</h3>
          <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20 }}>
            Please review the information below before submitting your
            authorization request.
          </p>

          <div className="form-section-title">Authorization Details</div>
          <div className="detail-grid" style={{ marginBottom: 24 }}>
            <div className="detail-row">
              <span className="detail-label">Member ID</span>
              <span className="detail-value">
                {formData.memberId || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Member Name</span>
              <span className="detail-value">
                {formData.memberName || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Service</span>
              <span className="detail-value">
                {formData.service || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Priority</span>
              <span className="detail-value">{formData.priority}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date From</span>
              <span className="detail-value">
                {formData.dateFrom || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date Through</span>
              <span className="detail-value">
                {formData.dateThrough || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Phone</span>
              <span className="detail-value">
                {formData.phone || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Fax</span>
              <span className="detail-value">{formData.fax || '--'}</span>
            </div>
          </div>

          <div className="form-section-title">Procedure & Diagnosis</div>
          <div className="detail-grid" style={{ marginBottom: 24 }}>
            <div className="detail-row">
              <span className="detail-label">Procedure Code</span>
              <span className="detail-value">
                {formData.procedureCode || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Procedure Description</span>
              <span className="detail-value">
                {formData.procedureDescription || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Diagnosis Code</span>
              <span className="detail-value">
                {formData.diagnosisCode || '--'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Diagnosis Description</span>
              <span className="detail-value">
                {formData.diagnosisDescription || '--'}
              </span>
            </div>
          </div>

          {formData.clinicalNotes && (
            <>
              <div className="form-section-title">Clinical Notes</div>
              <p
                style={{
                  color: '#1A1D21',
                  fontSize: 14,
                  lineHeight: 1.6,
                  background: '#F8FAFB',
                  padding: 16,
                  borderRadius: 10,
                  border: '1px solid #E8ECF0',
                }}
              >
                {formData.clinicalNotes}
              </p>
            </>
          )}
        </div>
      )}

      <div className="form-actions">
        {currentStep > 0 && (
          <button className="btn-secondary" onClick={handlePrevious}>
            Previous
          </button>
        )}
        {currentStep < steps.length - 1 && (
          <button className="btn-primary" onClick={handleNext}>
            Next
          </button>
        )}
        {currentStep === steps.length - 1 && (
          <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Authorization'}
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthorizationRequestPage;
